import React, { useState } from 'react';
import { StoryBible, Ledger, StoryMode, ChapterLength } from '../src/types';
import { checkNameSimilarity } from '../src/nameRegistry';

interface SequelSetupProps {
  initialBible: StoryBible;
  initialLedger: Ledger;
  onStart: (updatedBible: StoryBible, updatedLedger: Ledger) => void;
  onCancel: () => void;
}

const SequelSetup: React.FC<SequelSetupProps> = ({ initialBible, initialLedger, onStart, onCancel }) => {
  const [bible, setBible] = useState<StoryBible>({
    ...initialBible,
    eraState: initialBible.eraState || {
      currentVolume: initialBible.volume,
      currentEra: '',
      oldSystem: '',
      currentSystem: '',
      resolvedSystems: [],
      activeSystems: [],
      forbiddenEraConfusions: []
    },
    conceptBudget: initialBible.conceptBudget || {
      maxMajorNewSystemsPerVolume: 3,
      majorNewSystems: [],
      secondaryConcepts: [],
      derivedConcepts: []
    }
  });
  
  const normalizeLedger = (l: Ledger): Ledger => {
    const migrateArray = (arr: any[] | undefined) => {
      if (!arr) return [];
      return arr.map(item => {
        if (typeof item === 'string') {
          return { content: item, canonStatus: 'active' };
        }
        return item;
      });
    };
    return {
      ...l,
      inventory: migrateArray(l.inventory),
      plot_points: migrateArray(l.plot_points),
      resolved_plot_points: migrateArray(l.resolved_plot_points),
      historical_events: migrateArray(l.historical_events),
      characters: l.characters || {},
      locations: l.locations || {}
    };
  };

  const [ledger, setLedger] = useState<Ledger>(normalizeLedger(initialLedger));


  const [newCharName, setNewCharName] = useState('');
  const [newCharDesc, setNewCharDesc] = useState('');
  const [newLocName, setNewLocName] = useState('');
  const [newLocDesc, setNewLocDesc] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const finalValue = name === 'targetChapterCount' ? parseInt(value) || 1 : value;
    setBible(prev => ({ ...prev, [name]: finalValue }));
  };

  const handlePremiseChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setBible(prev => ({
      ...prev,
      premise: { ...prev.premise, [name]: value }
    }));
  };

  const [isInjecting, setIsInjecting] = useState(false);

  const handleAddCharacter = () => {
    if (!newCharName.trim() || !newCharDesc.trim()) return;
    
    const existingNames = Object.keys(ledger.characters || {});
    const similarityWarning = checkNameSimilarity(newCharName, existingNames);
    if (similarityWarning) {
      if (!window.confirm(`Name Similarity Warning: ${similarityWarning}nnDo you still want to add this character?`)) {
        return;
      }
    }

    setLedger(prev => ({
      ...prev,
      characters: {
        ...prev.characters,
        [newCharName]: {
          name: newCharName, // explicitly added for new convention
          desc: newCharDesc,
          status: 'Active',
          location: 'Unknown'
        }
      }
    }));
    setNewCharName('');
    setNewCharDesc('');
  };

  const handleAddLocation = () => {
    if (!newLocName.trim() || !newLocDesc.trim()) return;
    setLedger(prev => ({
      ...prev,
      locations: {
        ...prev.locations,
        [newLocName]: {
          desc: newLocDesc,
          status: 'Active'
        }
      }
    }));
    setNewLocName('');
    setNewLocDesc('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (bible.title && bible.premise.surface) {
      onStart(bible, ledger);
    }
  };

  return (
    <div className="min-h-screen bg-cv-page flex flex-col items-center justify-center p-6 text-cv-primary overflow-y-auto">
      <div className="max-w-4xl w-full bg-cv-panel border border-cv-border rounded-2xl p-8 my-8 shadow-2xl relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cv-interactive/30 via-cv-gold/20 to-cv-interactive/30"></div>
        
        <header className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-black text-cv-primary tracking-tighter uppercase">Volume {bible.volume} Configuration</h1>
            <p className="text-cv-muted font-mono text-[10px] mt-1 uppercase tracking-widest">Review and modify the sequel parameters</p>
          </div>
          <button onClick={onCancel} className="p-2 text-cv-muted hover:text-cv-primary transition-colors bg-cv-elevated rounded-lg border border-cv-border-strong">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </header>

        <form onSubmit={handleSubmit} className="space-y-10">
          <section className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Project Designation (Title)</label>
                <input name="title" value={bible.title} onChange={handleChange} className="w-full bg-cv-page border border-cv-border rounded-lg p-3 focus:border-cv-interactive outline-none transition-all font-bold text-cv-primary placeholder:text-cv-disabled" required />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Target Scale (Chapters)</label>
                <input type="number" name="targetChapterCount" value={bible.targetChapterCount} onChange={handleChange} min="1" max="100" className="w-full bg-cv-page border border-cv-border rounded-lg p-3 focus:border-cv-interactive outline-none transition-all text-cv-primary font-mono" required />
              </div>
            </div>
          </section>

          
          {/* Series Dashboard Review */}
          <section className="space-y-6">
            <h2 className="text-[11px] font-black text-cv-gold uppercase tracking-[0.2em] border-b border-cv-border pb-2 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cv-gold"></span> Current Series State
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-cv-input/50 rounded-xl border border-cv-border text-xs">
              <div>
                <span className="block text-[9px] uppercase tracking-widest text-cv-muted mb-1">Current Era</span>
                <span className="text-cv-primary font-medium">{bible.eraState?.currentEra || 'None'}</span>
              </div>
              <div>
                <span className="block text-[9px] uppercase tracking-widest text-cv-muted mb-1">Active Characters</span>
                <span className="text-cv-primary font-medium">{Object.values(ledger.characters || {}).filter(c => c.status === 'Active').length}</span>
              </div>
              <div>
                <span className="block text-[9px] uppercase tracking-widest text-cv-muted mb-1">Active Systems</span>
                <span className="text-cv-primary font-medium">{bible.eraState?.activeSystems?.length || 0}</span>
              </div>
              <div>
                <span className="block text-[9px] uppercase tracking-widest text-cv-muted mb-1">Resolved Systems</span>
                <span className="text-cv-primary font-medium">{bible.eraState?.resolvedSystems?.length || 0}</span>
              </div>
              <div className="col-span-2">
                <span className="block text-[9px] uppercase tracking-widest text-cv-muted mb-1">Ending Pattern</span>
                <span className="text-cv-primary font-medium">{bible.endingType || 'Not set'}</span>
              </div>
            </div>
          </section>

          {/* Era State Config */}
          <section className="space-y-6">
            <h2 className="text-[11px] font-black text-cv-gold uppercase tracking-[0.2em] border-b border-cv-border pb-2 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cv-gold"></span> Era State & Rules
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Current Era Name</label>
                <input value={bible.eraState?.currentEra || ''} onChange={e => setBible(prev => ({...prev, eraState: {...prev.eraState!, currentEra: e.target.value}}))} className="w-full bg-cv-page border border-cv-border rounded p-3 text-cv-primary focus:border-cv-warning outline-none" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Forbidden Era Confusions (Comma separated)</label>
                <input value={bible.eraState?.forbiddenEraConfusions?.join(', ') || ''} onChange={e => setBible(prev => ({...prev, eraState: {...prev.eraState!, forbiddenEraConfusions: e.target.value.split(',').map(s=>s.trim()).filter(Boolean)}}))} className="w-full bg-cv-page border border-cv-border rounded p-3 text-cv-primary focus:border-cv-warning outline-none" placeholder="e.g. No gunpowder, no steam power" />
              </div>
            </div>
          </section>

          {/* Concept Budget Config */}
          <section className="space-y-6">
            <h2 className="text-[11px] font-black text-cv-warning uppercase tracking-[0.2em] border-b border-cv-border pb-2 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cv-warning"></span> Concept Budget
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Max Major Systems</label>
                <input type="number" value={bible.conceptBudget?.maxMajorNewSystemsPerVolume || 3} onChange={e => setBible(prev => ({...prev, conceptBudget: {...prev.conceptBudget!, maxMajorNewSystemsPerVolume: parseInt(e.target.value) || 3}}))} className="w-full bg-cv-page border border-cv-border rounded p-3 text-cv-primary focus:border-cv-warning outline-none" />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Major New Systems (Comma separated)</label>
                <input value={bible.conceptBudget?.majorNewSystems?.join(', ') || ''} onChange={e => {
                  const systems = e.target.value.split(',').map(s=>s.trim()).filter(Boolean);
                  setBible(prev => ({...prev, conceptBudget: {...prev.conceptBudget!, majorNewSystems: systems}}));
                }} className="w-full bg-cv-page border border-cv-border rounded p-3 text-cv-primary focus:border-cv-warning outline-none" />
                {bible.conceptBudget && bible.conceptBudget.majorNewSystems.length > bible.conceptBudget.maxMajorNewSystemsPerVolume && (
                  <div className="text-cv-danger text-xs mt-1 font-bold">WARNING: Budget Exceeded ({bible.conceptBudget.majorNewSystems.length} / {bible.conceptBudget.maxMajorNewSystemsPerVolume})</div>
                )}
              </div>
            </div>
          </section>

          <section className="space-y-6">
            <h2 className="text-[11px] font-black text-cv-success uppercase tracking-[0.2em] border-b border-cv-border pb-2 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cv-success"></span> Evolved Narrative DNA
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Surface Conflict</label>
                <textarea name="surface" value={bible.premise.surface} onChange={handlePremiseChange} className="w-full bg-cv-page border border-cv-border rounded-lg p-4 h-32 focus:border-cv-interactive outline-none resize-none transition-all text-xs font-medium text-cv-primary" required />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Underlying Tension</label>
                <textarea name="tension" value={bible.premise.tension} onChange={handlePremiseChange} className="w-full bg-cv-page border border-cv-border rounded-lg p-4 h-32 focus:border-cv-interactive outline-none resize-none transition-all text-xs font-medium text-cv-primary" required />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-cv-muted uppercase">Irreversible Change</label>
                <textarea name="change" value={bible.premise.change} onChange={handlePremiseChange} className="w-full bg-cv-page border border-cv-border rounded-lg p-4 h-32 focus:border-cv-interactive outline-none resize-none transition-all text-xs font-medium text-cv-primary" required />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold text-cv-muted uppercase">Pre-made Chapter Roadmap (Optional)</label>
              <div className="text-[9px] text-cv-disabled mb-1">Paste a custom chapter roadmap. If provided, the Architect will adhere to this instead of inventing a new arc.</div>
              <textarea name="premadeRoadmap" value={bible.premadeRoadmap || ''} onChange={handleChange} className="w-full bg-cv-page border border-cv-border rounded-lg p-4 h-40 focus:border-cv-interactive outline-none resize-none transition-all text-xs font-mono text-cv-interactive placeholder:text-cv-disabled" placeholder="Chapter 1: The incident... &#10;Chapter 2: The journey begins... &#10;Chapter 3: The climax..." />
            </div>
          </section>

          <section className="space-y-6">
            <div className="flex justify-between items-center border-b border-cv-border pb-2">
              <h2 className="text-[11px] font-black text-cv-interactive uppercase tracking-[0.2em] flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-cv-interactive"></span> Ledger Injections
              </h2>
              <button type="button" onClick={() => setIsInjecting(!isInjecting)} className={`px-3 py-1 rounded text-[9px] font-black uppercase tracking-widest transition-all border ${isInjecting ? 'bg-cv-interactive border-cv-interactive text-cv-page' : 'bg-cv-elevated border-cv-border-strong text-cv-muted hover:text-cv-primary'}`}>
                {isInjecting ? 'Hide Injections' : '+ New Elements'}
              </button>
            </div>

            {isInjecting && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-cv-input/50 rounded-xl border border-cv-border">
                <div className="space-y-4">
                  <h3 className="text-[10px] font-black text-cv-muted uppercase tracking-widest mb-2 border-b border-cv-border pb-1">Inject Characters</h3>
                  <input placeholder="Name" value={newCharName} onChange={(e) => setNewCharName(e.target.value)} className="w-full bg-cv-page border border-cv-border rounded p-2 focus:border-cv-interactive outline-none text-xs text-cv-primary" />
                  <textarea placeholder="Description, appearance, background..." value={newCharDesc} onChange={(e) => setNewCharDesc(e.target.value)} className="w-full bg-cv-page border border-cv-border rounded p-2 h-16 focus:border-cv-interactive outline-none text-xs text-cv-primary resize-none" />
                  <button type="button" onClick={handleAddCharacter} className="w-full py-2 bg-cv-elevated hover:bg-cv-interactive text-cv-secondary hover:text-cv-primary text-[10px] font-black uppercase rounded transition-all">Add Character</button>
                </div>
                <div className="space-y-4">
                  <h3 className="text-[10px] font-black text-cv-muted uppercase tracking-widest mb-2 border-b border-cv-border pb-1">Inject Locations</h3>
                  <input placeholder="Location Name" value={newLocName} onChange={(e) => setNewLocName(e.target.value)} className="w-full bg-cv-page border border-cv-border rounded p-2 focus:border-cv-interactive outline-none text-xs text-cv-primary" />
                  <textarea placeholder="Description, atmosphere, connected history..." value={newLocDesc} onChange={(e) => setNewLocDesc(e.target.value)} className="w-full bg-cv-page border border-cv-border rounded p-2 h-16 focus:border-cv-interactive outline-none text-xs text-cv-primary resize-none" />
                  <button type="button" onClick={handleAddLocation} className="w-full py-2 bg-cv-elevated hover:bg-cv-interactive text-cv-secondary hover:text-cv-primary text-[10px] font-black uppercase rounded transition-all">Add Location</button>
                </div>
              </div>
            )}
            
            {(Object.keys(ledger.characters).length > 0 || Object.keys(ledger.locations).length > 0) && isInjecting && (
                <div className="text-[10px] font-mono text-cv-muted h-24 overflow-y-auto mt-4 px-2 custom-scrollbar">
                  Current Cast: {Object.keys(ledger.characters || {}).join(', ')}<br/>
                  Current Map: {Object.keys(ledger.locations || {}).join(', ')}
                </div>
            )}

          </section>

          <button type="submit" className="w-full py-5 bg-cv-interactive hover:bg-cv-interactive-hover text-cv-page font-black text-sm uppercase tracking-[0.3em] rounded-xl transition-all shadow-2xl shadow-black/30 transform active:scale-[0.98] flex items-center justify-center gap-3">
            CONFIRM AND BEGIN VOLUME {bible.volume}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SequelSetup;

